"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Download, FileText, Play } from "lucide-react";
import { IconClose, IconLink, IconSpinner, IconVideo} from "@/components/ui/icons";
import { AuthImage } from "@/components/ui/auth-image";
import { api } from "@/lib/api";
import { downloadAuthFile, useAuthBlobUrl as useAuthBlob } from "@/lib/auth-blob";
import type { TeamAttachment } from "@/lib/types";
import { Z } from "@/lib/z";

// ── Общий рендер вложений (картинка/файл/ссылка) ─────────────────────────────
// Переиспользуется в командной панели (задачи/чат) и в заявках на доработку.
export async function downloadTeamFile(att: TeamAttachment) {
  try {
    await downloadAuthFile(api.teamFileUrl(att.url), att.original_name || att.filename, { mode: "any" });
  } catch { /* ignore */ }
}

// fetch приватного файла с Bearer → object URL (для <video>/полного просмотра)
export function useAuthBlobUrl(path: string | null): string | null {
  return useAuthBlob(path ? api.teamFileUrl(path) : null, { mode: "any" }).url;
}

// Полноэкранный просмотр картинки/видео по клику
export function Lightbox({ att, onClose }: { att: TeamAttachment; onClose: () => void }) {
  const videoUrl = useAuthBlobUrl(att.kind === "video" ? att.url : null);
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  if (typeof document === "undefined") return null;
  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center bg-black/80 p-4" style={{ zIndex: Z.LIGHTBOX }} onClick={onClose}>
      <button type="button" onClick={onClose} aria-label="Закрыть" className="absolute right-4 top-4 text-white/80 hover:text-white"><IconClose size={26} /></button>
      <div onClick={(e) => e.stopPropagation()} className="max-h-[92dvh] max-w-[92vw]">
        {att.kind === "video" ? (
          videoUrl ? <video src={videoUrl} controls autoPlay className="max-h-[92dvh] max-w-[92vw] rounded-[var(--radius-lg)]" />
            : <IconSpinner className="animate-spin text-white" size={32} />
        ) : (
          <AuthImage src={att.url} alt={att.original_name} className="max-h-[92dvh] max-w-[92vw] rounded-[var(--radius-lg)] object-contain" />
        )}
      </div>
    </div>,
    document.body,
  );
}

export function AttachmentList({ items, onDelete }: { items: TeamAttachment[]; onDelete?: (id: number) => void }) {
  const [lb, setLb] = useState<TeamAttachment | null>(null);
  const [hover, setHover] = useState<{ att: TeamAttachment; x: number; y: number } | null>(null);
  if (!items?.length) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {items.map((a) => (
        <div key={a.id} className="group/att relative">
          {a.kind === "image" ? (
            <div
              onMouseEnter={(e) => { const r = e.currentTarget.getBoundingClientRect(); setHover({ att: a, x: r.left, y: r.top }); }}
              onMouseLeave={() => setHover(null)}
              className="h-16 w-16 cursor-pointer"
              onClick={() => setLb(a)}>
              <AuthImage src={a.url} alt={a.original_name}
                className="h-16 w-16 rounded-[var(--radius-lg)] border border-[var(--border)] object-cover" />
            </div>
          ) : a.kind === "video" ? (
            <button type="button" onClick={() => setLb(a)} data-tooltip={a.original_name}
              className="relative flex h-16 w-16 items-center justify-center rounded-[var(--radius-lg)] border border-[var(--border)] bg-black/60 text-white">
              <IconVideo size={18} className="opacity-60" />
              <Play size={20} className="absolute" fill="currentColor" />
            </button>
          ) : a.kind === "link" ? (
            <a href={a.url} target="_blank" rel="noopener noreferrer"
              className="inline-flex max-w-[180px] items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surfaceElevated)] px-2.5 py-1 text-[11px] text-[var(--primary)] hover:underline">
              <IconLink size={12} /><span className="truncate">{a.original_name}</span>
            </a>
          ) : (
            <button type="button" onClick={() => void downloadTeamFile(a)} data-tooltip={a.original_name}
              className="inline-flex max-w-[180px] items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surfaceElevated)] px-2.5 py-1 text-[11px] text-[var(--text)]">
              <FileText size={12} /><span className="truncate">{a.original_name}</span><Download size={11} className="opacity-60" />
            </button>
          )}
          {onDelete && (
            <button type="button" onClick={() => onDelete(a.id)} aria-label="Удалить вложение"
              className="absolute -right-1.5 -top-1.5 hidden h-4 w-4 items-center justify-center rounded-full bg-[var(--danger)] text-white group-hover/att:flex">
              <IconClose size={10} />
            </button>
          )}
        </div>
      ))}
      {lb && <Lightbox att={lb} onClose={() => setLb(null)} />}
      {hover && typeof document !== "undefined" && createPortal(
        (() => {
          const W = 260, H = 230;
          const left = Math.max(8, Math.min(hover.x, window.innerWidth - W - 8));
          const top = hover.y - H - 8 > 8 ? hover.y - H - 8 : hover.y + 72 + 8; // над миниатюрой, иначе под
          return (
            <div style={{ position: "fixed", left, top, zIndex: Z.LIGHTBOX + 2, pointerEvents: "none" }}>
              <AuthImage src={hover.att.url} alt={hover.att.original_name}
                className="max-h-[220px] max-w-[260px] rounded-[var(--radius-lg)] border border-[var(--border)] object-contain elev-modal" />
            </div>
          );
        })(),
        document.body,
      )}
    </div>
  );
}
